import { Timer } from 'lucide-react';
import { useState } from 'react';
import { PulsingDot } from '@/components/custom/pulsing-dot';
import { TimerPopover } from '@/components/custom/timer-popover';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMe } from '@/lib/me';
import {
  formatElapsed,
  useCustomerNameLookup,
  useLiveElapsed,
  useProductNameLookup,
  useTimers,
  useTimersFetchedAt,
} from '@/lib/timer-hooks';
import { cn } from '@/lib/utils';

/**
 * Always-visible heads-up display at the top center of the viewport.
 * One pill, three states (mutually exclusive):
 *
 *   Running  — clock + pulsing dot + live elapsed. Hover reveals
 *              customer / product / description; click opens the
 *              full timer popover (pause / commit / discard).
 *   Paused   — clock + "Paused timers (N)". Click opens the popover.
 *   Empty    — clock + "New timer". Click opens the same popover,
 *              showing `TimerPopover`'s `DraftTimerCard`; the timer
 *              itself only starts once its "Start timer" button is
 *              clicked there, and the popover closes right after
 *              (`onTimerStarted` below) instead of lingering open on
 *              the timer the user just started.
 *
 * All three states share one `Popover` around `TimerPopover` — only the
 * trigger pill (and, for running/paused, a hover tooltip) differs.
 *
 * Replaces the per-page sidebar timer entry: now visible on every
 * authenticated page, sidebar collapsed or not. Hidden entirely when
 * the user lacks `timer.view`.
 */
export function TimerHud({ offsetTop = 0 }: { offsetTop?: number }) {
  const { permissions } = useMe();
  const canView = permissions.has('timer.view');
  const canEdit = permissions.has('timer.edit');
  const { running, pausedTotal } = useTimers();
  // Shift below the announcement + impersonation banners so the pill isn't
  // hidden by them. Both are `fixed z-50` siblings stacked at the top; the HUD
  // is `fixed z-40`. `AppLayout` passes their combined height — the same
  // value it reserves as top padding on the shell.

  // Hooks ABOVE the conditional return — react requires stable hook order.
  const baseAt = useTimersFetchedAt();
  const elapsed = useLiveElapsed(running?.elapsedSeconds ?? 0, baseAt, Boolean(running));
  const customerName = useCustomerNameLookup(running?.timer.customerId ?? null);
  const productName = useProductNameLookup(running?.timer.productId ?? null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (!canView) return null;

  const hasRunning = running != null;
  const hasPaused = pausedTotal > 0;
  const isEmpty = !hasRunning && !hasPaused;
  // Empty + read-only: there's nothing to view and the user can't start
  // a timer either, so the hud collapses away entirely rather than
  // dangling a non-clickable badge in the chrome.
  if (isEmpty && !canEdit) return null;

  const description = running?.timer.description ?? null;
  const hasDetails = hasRunning && Boolean(customerName || productName || description);

  const trigger = isEmpty ? (
    <button type="button" aria-label="Open new timer" className={pillClass()}>
      <Timer className="size-3.5" />
      <span className="font-medium">New timer</span>
    </button>
  ) : (
    <button
      type="button"
      aria-label={hasRunning ? 'Open running timer' : `Open paused timers (${pausedTotal})`}
      className={pillClass(hasRunning ? 'running' : 'paused')}
    >
      <Timer className="size-3.5" />
      {hasRunning ? (
        <>
          <PulsingDot size="sm" />
          <span className="font-mono font-semibold tabular-nums">{formatElapsed(elapsed)}</span>
        </>
      ) : (
        <span className="font-medium">Paused timers ({pausedTotal})</span>
      )}
    </button>
  );

  return (
    <Container topOffset={offsetTop}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        {isEmpty ? (
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            </TooltipTrigger>
            {hasRunning && hasDetails && !popoverOpen ? (
              <TooltipContent
                side="bottom"
                sideOffset={6}
                className="max-w-[280px] px-3 py-2 text-left text-xs leading-relaxed"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">{customerName ?? 'No customer'}</span>
                  {productName ? (
                    <>
                      <span className="opacity-60">·</span>
                      <span className="opacity-90">{productName}</span>
                    </>
                  ) : null}
                </div>
                {description ? (
                  <div className="mt-1 line-clamp-3 opacity-80">{description}</div>
                ) : null}
              </TooltipContent>
            ) : null}
          </Tooltip>
        )}
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={4}
          className="z-50 w-auto rounded-lg p-0"
        >
          <TimerPopover onTimerStarted={() => setPopoverOpen(false)} />
        </PopoverContent>
      </Popover>
    </Container>
  );
}

function Container({ children, topOffset }: { children: React.ReactNode; topOffset: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ top: topOffset }}
      className="pointer-events-none fixed left-1/2 z-40 -translate-x-1/2"
    >
      {children}
    </div>
  );
}

function pillClass(variant: 'running' | 'paused' | 'empty' = 'empty'): string {
  return cn(
    'pointer-events-auto inline-flex h-7 cursor-pointer items-center gap-2 rounded-b-md border border-t-0 px-3 text-sm shadow-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default disabled:opacity-70',
    // Empty state (no timer running) reads as the primary call-to-action —
    // same fill + hover as every other primary Button in the app
    // (`bg-primary hover:bg-primary/90`). `light:` is required, not
    // cosmetic: it matches the specificity of `light:bg-card` below, so
    // whichever branch applies actually wins instead of losing to it.
    variant === 'empty'
      ? 'border-border light:bg-primary text-primary-foreground hover:bg-primary/90'
      : // `light:bg-card` (white), not `bg-background` — the latter is the cream page,
        // so the pill dissolved into the backdrop it's meant to float over.
        'light:bg-card text-foreground hover:bg-accent',
    variant === 'running' && 'border-red-200/80 dark:border-red-500/30',
    variant === 'paused' && 'border-border',
  );
}
